import React, { FC, MouseEvent, useState, useCallback } from 'react';
import styled from 'styled-components';
import Tooltip from '@material-ui/core/Tooltip';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import CheckDecagramIcon from 'mdi-react/CheckDecagramIcon';
import OpenInNewIcon from 'mdi-react/OpenInNewIcon';
import InfoCircleIcon from 'mdi-react/InfoCircleIcon';
import HelpIcon from 'mdi-react/HelpIcon';
import WarningIcon from 'mdi-react/WarningIcon';
import DangerousIcon from 'mdi-react/DangerousIcon';
import DiamondIcon from 'mdi-react/DiamondIcon';
import { Field, Skeleton } from './CompanyFieldAndHelpers';
import { Grid, Column, Item } from './CompanyGrid';
import { WWWCompanyDocument } from '../../../common/documents/WWWCompanyDocument';
import { FormattedMessage } from 'react-intl';
import { useUser } from '../../context/useUser';
import { Color } from '../Theme';

const ReportTooltipStyle = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.6rem 0.7rem 0.5rem 0.7rem;
  align-items: center;
  text-align: center;
  font-size: 0.875rem;
  line-height: 1.4;

  > svg {
    position: absolute;
    margin: 0 !important;
    padding: 0.5rem;
    top: -0.8rem;
    right: -0.8rem;
    background-color: ${Color.white};
    border-radius: 50%;
    box-sizing: content-box;
  }
`;

interface Props {
  company: WWWCompanyDocument;
  editable: boolean;
  premium: boolean;
  loading: boolean;
  trustInfo: any | null;
  isPremiumOverride: boolean;
}

const ReportTooltip: FC = () => (
  <ReportTooltipStyle>
    <DiamondIcon color={Color.primary} />
    <FormattedMessage id="company.trustInfo.reportUnauthorizedTooltip" defaultMessage="Raportti on saatavilla Constle Pro -jäsenille" />
  </ReportTooltipStyle>
);

export const CompanyTrustInfo: FC<Props> = ({ company, premium, loading, trustInfo, isPremiumOverride }) => {
  const { hasPremium } = useUser();
  const [reportTooltipOpen, setReportTooltipOpen] = useState(false);

  const commonFieldProps = {
    premium,
    loading,
  };

  const preventReportDownload = !hasPremium && isPremiumOverride;

  const handleReportLinkClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      // Prevent download & show tooltip if company is in "preview" mode
      if (preventReportDownload) {
        event.preventDefault();
        setReportTooltipOpen(!reportTooltipOpen);
      }
    },
    [preventReportDownload]
  );

  const reportLink = (
    <a
      href={preventReportDownload ? '#' : `/api/v1/company/tilaajavastuu/pdf/${company.businessID}`}
      download
      onClick={handleReportLinkClick}
    >
      <span>Lataa raportti</span>
      <OpenInNewIcon size={18} color={Color.disabled} />
    </a>
  );

  return (
    <Grid>
      <Column>
        <Grid>
          <Column>
            <Item>
              <Field label={'Tila'} {...commonFieldProps} large premiumOnly>
                {!premium || loading ? (
                  <>
                    <Skeleton loading={loading} width="30%" />
                  </>
                ) : trustInfo === null ? (
                  <>
                    <span>-</span>
                  </>
                ) : (
                  <>
                    <span>{trustInfo.status !== 'not found' ? trustInfo.status : '-'}</span>
                    {trustInfo.status == 'Ok' && <CheckDecagramIcon color={Color.green} />}
                    {trustInfo.status == 'Huomioi' && <InfoCircleIcon color="#44f" />}
                    {trustInfo.status == 'Tietoja odotetaan' && <HelpIcon color="#aaa" />}
                    {trustInfo.status == 'Selvitä' && <WarningIcon color="#aa0" />}
                    {trustInfo.status == 'Seis' && <DangerousIcon color="#a00" />}
                  </>
                )}
              </Field>
            </Item>
          </Column>
          <Column>
            <Item>
              <Field label={'Tarkistettu'} {...commonFieldProps} premiumOnly>
                {!premium || loading ? <Skeleton loading={loading} width="50%" /> : new Date().toLocaleDateString('fi-fi')}
              </Field>
            </Item>
          </Column>
        </Grid>
      </Column>
      <Column>
        <Item>
          <Field label={'Raportti'} {...commonFieldProps} large premiumOnly>
            {!premium || loading ? (
              <Skeleton loading={loading} width="80%" />
            ) : !trustInfo || trustInfo.status === 'not found' ? (
              <span>Raportti ei saatavilla</span>
            ) : preventReportDownload ? (
              <ClickAwayListener onClickAway={() => setReportTooltipOpen(false)}>
                <Tooltip
                  PopperProps={{
                    disablePortal: true,
                  }}
                  onClose={() => setReportTooltipOpen(false)}
                  open={reportTooltipOpen}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  title={<ReportTooltip />}
                  arrow
                >
                  {reportLink}
                </Tooltip>
              </ClickAwayListener>
            ) : (
              reportLink
            )}
          </Field>
        </Item>
      </Column>
    </Grid>
  );
};
